import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@material-ui/core";
import {
  ReskinningInputs,
  CustomAsyncAutocomplete,
  CustomButton,
} from "../../../../../Components";

import useStyles from "./styles";
import { useTranslate } from "../../../../../Hooks";
import { GetLookupItemsByLookupTypeName } from "../../../../../Services";
import {
  InvoiceStatus,
  CollectionStatus,
  PaymentStatus,
} from "../../../../../Enums/dealStatus.Enum";
import DiscardDialog from "../../DiscardDialog/DiscardDialog";

function Invoice({
  data,
  selectedTransaction,
  onSave,
  index,
  validateFields,
  formValue,
  setFormValue,
  isOpen,
}) {
  const { translate } = useTranslate("AgentCommissionView");
  const [error, setError] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { isDarkMode } = useSelector((state) => state.theme);
  const styles = useStyles({ isDarkMode });

  useEffect(() => {
    setError(validateFields(formValue));
  }, [formValue]);
  // Helper to get initial form values from data
  const getInitialFormValue = (data) => ({
    invoicesNumber: data.invoicesNumber || "",
    voucherNumber: data.voucherNumber || "",
    invoiceStatus:
      {
        lookupItemName:
          InvoiceStatus[data.invoiceStatus]?.label ||
          data.invoiceStatus?.lookupItemName,
        lookupItemId:
          InvoiceStatus[data.invoiceStatus]?.id ||
          data.invoiceStatus?.lookupItemId,
      } || null,
    collectionStatus:
      {
        lookupItemName:
          CollectionStatus[data.collectionStatus]?.label ||
          data.collectionStatus?.lookupItemName,
        lookupItemId:
          CollectionStatus[data.collectionStatus]?.id ||
          data.collectionStatus?.lookupItemId,
      } || null,
    paymentStatus:
      {
        lookupItemName:
          PaymentStatus[data.paymentStatus]?.label ||
          data.paymentStatus?.lookupItemName,
        lookupItemId:
          PaymentStatus[data.paymentStatus]?.id ||
          data.paymentStatus?.lookupItemId,
      } || null,
    agencyFeesRate: data.agencyFeesRate || "0",
    agentCommissionRate: data.agentCommissionRate || "0",
    deduction: data.deduction || "0",
    totalDealExpense: data.totalDealExpense || "0",
    adminFees: data.adminFees || "0",
    listingCommission: data.listingCommission || "0",
    telesalesCommission: data.telesalesCommission || "0",
    coordinatorCommission: data.coordinatorCommission || "0",
    tlCommissionRate: data.tlCommissionRate || "0",
    smCommissionRate: data.smCommissionRate || "0",
  });

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setFormValue(getInitialFormValue(data));
    } else {
      setFormValue({});
    }
  }, [index]);
  return (
    <Box className={styles.fieldsContainer}>
      <Box className={styles.fieldSection}>
        <Typography className={styles.fieldSectionHeader}>
          {translate("status-and-tracking")}
        </Typography>
        {/* Status & Tracking section */}
        <ReskinningInputs
          labelValue={translate("invoice-number")}
          inputPlaceholder={translate("enter")}
          value={formValue.invoicesNumber}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, invoicesNumber: e.target.value })
          }
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("receipt-voucher")}
          inputPlaceholder={translate("enter")}
          value={formValue.voucherNumber}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, voucherNumber: e.target.value })
          }
          type={"number"}
        />
        <CustomAsyncAutocomplete
          labelValue={translate("invoice-status")}
          selectedValues={formValue.invoiceStatus}
          rootCustomClasses={styles.fieldSectionInputs}
          onChange={(_e, value) =>
            setFormValue({ ...formValue, invoiceStatus: value })
          }
          disabled={!isOpen}
          mapper={(data) =>
            data.map(({ lookupItemName, lookupItemId }) => ({
              lookupItemName,
              lookupItemId,
            }))
          }
          placeholder={translate("select")}
          onFetchData={GetLookupItemsByLookupTypeName}
          filter={{
            lookUpName: "AgentAchievementCommissionInvoiceStatus",
            pageSize: 25,
            pageIndex: 1,
          }}
          displayLabel={(option) => option?.lookupItemName ?? ""}
          renderOption={(option) => option?.lookupItemName}
          chipsLabel={(option) => option?.lookupItemName}
          getOptionSelected={(option, value) =>
            option?.lookupItemId === value?.lookupItemId
          }
        />
        <CustomAsyncAutocomplete
          labelValue={translate("collection-status")}
          selectedValues={formValue.collectionStatus}
          rootCustomClasses={styles.fieldSectionInputs}
          onChange={(_e, value) =>
            setFormValue({ ...formValue, collectionStatus: value })
          }
          disabled={!isOpen}
          mapper={(data) =>
            data.map(({ lookupItemName, lookupItemId }) => ({
              lookupItemName,
              lookupItemId,
            }))
          }
          placeholder={translate("select")}
          onFetchData={GetLookupItemsByLookupTypeName}
          filter={{
            lookUpName: "AgentAchievementCommissionInvoiceCollectionStatus",
            pageSize: 25,
            pageIndex: 1,
          }}
          displayLabel={(option) => option?.lookupItemName ?? ""}
          renderOption={(option) => option?.lookupItemName}
          chipsLabel={(option) => option?.lookupItemName}
          getOptionSelected={(option, value) =>
            option?.lookupItemId === value?.lookupItemId
          }
        />
        <CustomAsyncAutocomplete
          labelValue={translate("payment-status")}
          selectedValues={formValue.paymentStatus}
          rootCustomClasses={styles.fieldSectionInputs}
          onChange={(_e, value) =>
            setFormValue({ ...formValue, paymentStatus: value })
          }
          disabled={!isOpen}
          mapper={(data) =>
            data.map(({ lookupItemName, lookupItemId }) => ({
              lookupItemName,
              lookupItemId,
            }))
          }
          placeholder={translate("select")}
          onFetchData={GetLookupItemsByLookupTypeName}
          filter={{
            lookUpName: "AgentAchievementCommissionInvoicePaymentStatus",
            pageSize: 25,
            pageIndex: 1,
          }}
          displayLabel={(option) => option?.lookupItemName ?? ""}
          renderOption={(option) => option?.lookupItemName}
          chipsLabel={(option) => option?.lookupItemName}
          getOptionSelected={(option, value) =>
            option?.lookupItemId === value?.lookupItemId
          }
        />
      </Box>
      <Box className={styles.fieldSection}>
        {/* Agent commission breakdown */}
        <Typography className={styles.fieldSectionHeader}>
          {translate("agent-commission-breakdown")}
        </Typography>
        <ReskinningInputs
          labelValue={translate("agency-fee-rate")}
          inputPlaceholder={translate("percent-enter")}
          value={formValue.agencyFeesRate}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, agencyFeesRate: e.target.value })
          }
          startAdornment="%"
          error={!!error.agencyFeesRate}
          helperText={error.agencyFeesRate}
          isWithError={!!error.agencyFeesRate}
          withNumberFormat
          type={"number"}
        />

        <ReskinningInputs
          labelValue={translate("agent-commission-rate")}
          inputPlaceholder={translate("percent-enter")}
          value={formValue.agentCommissionRate}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({
              ...formValue,
              agentCommissionRate: e.target.value,
            })
          }
          startAdornment="%"
          error={!!error.agentCommissionRate}
          helperText={error.agentCommissionRate}
          isWithError={!!error.agentCommissionRate}
          withNumberFormat
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("deduction-amount")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.deduction}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, deduction: e.target.value })
          }
          error={!!error.deduction}
          helperText={error.deduction}
          isWithError={!!error.deduction}
          withNumberFormat
          type={"number"}
        />
      </Box>
      <Box className={styles.fieldSection}>
        {/* Deal & Admin */}
        <Typography className={styles.fieldSectionHeader}>
          {translate("deal-admin")}
        </Typography>
        <ReskinningInputs
          labelValue={translate("total-deal-expenses")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.totalDealExpense}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, totalDealExpense: e.target.value })
          }
          withNumberFormat
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("admin-fees")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.adminFees}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, adminFees: e.target.value })
          }
          error={!!error.adminFees}
          helperText={error.adminFees}
          isWithError={!!error.adminFees}
          withNumberFormat
          type={"number"}
        />
      </Box>
      <Box className={styles.fieldSection}>
        {/* Other commission */}
        <Typography className={styles.fieldSectionHeader}>
          {translate("other-commission")}
        </Typography>
        <ReskinningInputs
          labelValue={translate("listing-commission")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.listingCommission}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, listingCommission: e.target.value })
          }
          error={!!error.listingCommission}
          helperText={error.listingCommission}
          isWithError={!!error.listingCommission}
          withNumberFormat
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("telesales-commission")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.telesalesCommission}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({
              ...formValue,
              telesalesCommission: e.target.value,
            })
          }
          error={!!error.telesalesCommission}
          helperText={error.telesalesCommission}
          isWithError={!!error.telesalesCommission}
          withNumberFormat
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("coordinator-commission")}
          inputPlaceholder={translate("aed-amount")}
          value={formValue.coordinatorCommission}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({
              ...formValue,
              coordinatorCommission: e.target.value,
            })
          }
          error={!!error.coordinatorCommission}
          helperText={error.coordinatorCommission}
          isWithError={!!error.coordinatorCommission}
          withNumberFormat
          type={"number"}
        />
      </Box>
      <Box className={styles.fieldSection}>
        {/* Team leader & SM */}
        <Typography className={styles.fieldSectionHeader}>
          {translate("team-leader-sm")}
        </Typography>
        <ReskinningInputs
          labelValue={translate("tl-commission-rate")}
          inputPlaceholder={translate("percent-eg")}
          value={formValue.tlCommissionRate}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, tlCommissionRate: e.target.value })
          }
          startAdornment="%"
          error={!!error.tlCommissionRate}
          helperText={error.tlCommissionRate}
          isWithError={!!error.tlCommissionRate}
          withNumberFormat
          type={"number"}
        />
        <ReskinningInputs
          labelValue={translate("sm-commission-rate")}
          inputPlaceholder={translate("percent-eg")}
          value={formValue.smCommissionRate}
          rootCustomClasses={styles.fieldSectionInputs}
          onInputChanged={(e) =>
            setFormValue({ ...formValue, smCommissionRate: e.target.value })
          }
          startAdornment="%"
          error={!!error.smCommissionRate}
          helperText={error.smCommissionRate}
          isWithError={!!error.smCommissionRate}
          withNumberFormat
          type={"number"}
        />
      </Box>
      <Box flexDirection={"row"} className={styles.buttonContainer}>
        <CustomButton
          boxShadow="xs"
          size="md"
          variant={"outlined"}
          color={"secondary"}
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          {translate("discard")}
        </CustomButton>
        <CustomButton
          size="md"
          variant={"primary"}
          color={"primary"}
          disabled={Object.keys(error).length > 0}
          onClick={() => {
            onSave(formValue, index);
          }}
        >
          {translate("save")}
        </CustomButton>
      </Box>
      <DiscardDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onDiscardAction={() => {
          setFormValue(
            getInitialFormValue(selectedTransaction?.invoices?.[index] || {})
          );
        }}
      />
    </Box>
  );
}

export default Invoice;
